import type {
  Run, Schedule, ScheduleInput, Settings, Tier,
} from "../server/types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  const data = text ? JSON.parse(text) : {};
  if (!resp.ok) {
    const msg = (data && data.error) ? data.error : `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  getMe: () => request<{ user: { id: number; username: string } | null }>("/api/auth/me"),
  bootstrapStatus: () => request<{ needsBootstrap: boolean }>("/api/auth/bootstrap"),
  bootstrap: (username: string, password: string) =>
    request<{ user: { id: number; username: string } }>("/api/auth/bootstrap", {
      method: "POST", body: JSON.stringify({ username, password }),
    }),
  login: (username: string, password: string) =>
    request<{ user: { id: number; username: string } }>("/api/auth/login", {
      method: "POST", body: JSON.stringify({ username, password }),
    }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  getSchedules: () => request<{ schedules: Schedule[] }>("/api/schedules"),
  createSchedule: (s: ScheduleInput) =>
    request<{ schedule: Schedule }>("/api/schedules", { method: "POST", body: JSON.stringify(s) }),
  updateSchedule: (id: number, s: ScheduleInput) =>
    request<{ ok: true }>(`/api/schedules/${id}`, { method: "PUT", body: JSON.stringify(s) }),
  deleteSchedule: (id: number) =>
    request<{ ok: true }>(`/api/schedules/${id}`, { method: "DELETE" }),

  getRuns: (limit = 100) => request<{ runs: Run[] }>(`/api/runs?limit=${limit}`),
  getRun: (id: number) => request<{ run: Run }>(`/api/runs/${id}`),

  getBackups: () =>
    request<{ backups: Record<Tier, { key: string; size: number; lastModified: string }[]> }>(
      "/api/backups",
    ),
  getDownloadUrl: (key: string) =>
    request<{ url: string; expiresIn: number }>(
      `/api/backups/download?key=${encodeURIComponent(key)}`,
    ),

  getSettings: () => request<{ settings: Settings }>("/api/settings"),
  updateSettings: (patch: Partial<Settings>) =>
    request<{ settings: Settings }>("/api/settings", { method: "PUT", body: JSON.stringify(patch) }),

  trigger: (tier: Tier) =>
    request<{ accepted: boolean; reason?: string }>("/api/trigger", {
      method: "POST", body: JSON.stringify({ tier }),
    }),
  triggerStatus: () => request<{ inFlight: boolean }>("/api/trigger"),

  replicaStatus: () =>
    request<
      | { configured: false }
      | {
          configured: true; reachable: true;
          lagSeconds: number; lagThresholdSeconds: number; withinThreshold: boolean;
        }
      | { configured: true; reachable: false; error: string }
    >("/api/health/replica"),
};
